import React, { useState } from "react";
import { Box, TextField, Button, Typography } from "@mui/material";
import { collection, addDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../../../firestore-DB-config/firebase-config"; // Ensure your Firestore and Storage instances are correctly imported
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";

const ReviewForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    heading: "",
    message: "",
    avatar: null as File | null, // Store the image file
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [avatarPreview, setAvatarPreview] = useState<string>("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (files && name === "avatar") {
      const file = files[0];
      setFormData((prevData) => ({
        ...prevData,
        avatar: file,
      }));

      // Generate a preview URL for the avatar image
      const reader = new FileReader();
      reader.onload = () => setAvatarPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `avatars/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let avatarUrl = "";

      if (formData.avatar) {
        avatarUrl = await uploadImage(formData.avatar);
      }

      // Firestore operation
      const reviewData = {
        name: formData.name,
        role: formData.role,
        heading: formData.heading,
        message: formData.message,
        avatar: avatarUrl, // Use the URL obtained from Firebase Storage
      };

      await addDoc(collection(db, "testimonials"), reviewData);

      // Clear form fields and reset avatar preview after successful submission
      setFormData({
        name: "",
        role: "",
        heading: "",
        message: "",
        avatar: null,
      });
      setAvatarPreview("");
      setUploadProgress(0);
      setLoading(false);
      console.log("Review submitted successfully!");
    } catch (err) {
      setError("Failed to submit review. Please try again.");
      console.error("Error submitting review: ", err);
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: 600,
          margin: "auto",
          my: 4,
          p: 3,
          boxShadow: 2,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" mb={2}>
          Submit Your Review
        </Typography>
        <TextField
          fullWidth
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          label="Role"
          name="role"
          value={formData.role}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          label="Heading"
          name="heading"
          value={formData.heading}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          label="Message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          multiline
          rows={4}
          sx={{ mb: 2 }}
          required
        />

        {/* Avatar Image Input with Preview */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          Avatar Image
        </Typography>
        <input
          type="file"
          name="avatar"
          accept="image/*"
          onChange={handleInputChange}
        />
        {avatarPreview && (
          <Box mt={2}>
            <img
              src={avatarPreview}
              alt="Avatar Preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Box>
        )}
        {uploadProgress > 0 && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Uploading Avatar Image: {uploadProgress.toFixed(2)}%
          </Typography>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          sx={{ width: "100%", mt: 3 }}
        >
          {loading ? "Submitting..." : "Submit Review"}
        </Button>
      </Box>
      <Footer />
    </>
  );
};

export default ReviewForm;
